/**
 * Platform Error Context
 *
 * Provides centralized error reporting and coordination across widget containers.
 * Errors are tagged with their source (dashboard, global) for context-aware rendering.
 *
 * Key features:
 * - Deduplication by accountId (multiple widgets with same account = one error)
 * - Source-aware filtering (dashboard errors show in dashboard banner)
 * - Automatic source injection from WidgetSourceProvider
 *
 * @see .work/specs/M9/005-widget-platform-integration.md
 */
'use client';

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from 'react';

import type { ClassifiedError, ErrorSource } from '../types/classified-error';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface PlatformError {
  /** Unique account identifier */
  accountId: string;
  /** Display name for the account */
  accountName: string;
  /** Provider identifier (e.g., 'github') */
  provider: string;
  /** The classified error */
  error: ClassifiedError;
  /** Source context where error was reported */
  source: ErrorSource;
  /** OAuth URL to reconnect the account */
  reconnectUrl: string;
}

interface PlatformErrorContextValue {
  /** All current platform errors keyed by accountId */
  errors: Map<string, PlatformError>;

  /** Report a token error (source auto-detected from WidgetSourceContext) */
  reportError: (accountId: string, error: ClassifiedError) => void;

  /** Clear error for an account (after successful re-auth) */
  clearError: (accountId: string) => void;

  /** Get errors filtered by source */
  getErrorsForSource: (source: ErrorSource) => PlatformError[];
}

// -----------------------------------------------------------------------------
// Contexts
// -----------------------------------------------------------------------------

const PlatformErrorContext = createContext<PlatformErrorContextValue | null>(
  null
);
PlatformErrorContext.displayName = 'PlatformErrorContext';

const WidgetSourceContext = createContext<ErrorSource>('dashboard');
WidgetSourceContext.displayName = 'WidgetSourceContext';

// -----------------------------------------------------------------------------
// Providers
// -----------------------------------------------------------------------------

interface PlatformErrorProviderProps {
  children: ReactNode;
}

/**
 * Provider for platform-level error coordination.
 *
 * Mount at app root level, inside QueryClientProvider.
 *
 * @example
 * <QueryClientProvider client={queryClient}>
 *   <PlatformErrorProvider>
 *     {children}
 *   </PlatformErrorProvider>
 * </QueryClientProvider>
 */
export function PlatformErrorProvider({
  children,
}: PlatformErrorProviderProps) {
  const [errors, setErrors] = useState<Map<string, PlatformError>>(
    () => new Map()
  );

  const reportError = useCallback(
    (accountId: string, error: ClassifiedError) => {
      setErrors(prev => {
        const next = new Map(prev);
        next.set(accountId, {
          accountId,
          accountName: error.details?.accountName ?? accountId,
          provider: error.details?.provider ?? 'unknown',
          error,
          source: error.details?.source ?? 'dashboard',
          reconnectUrl: error.details?.reconnectUrl ?? '',
        });
        return next;
      });
    },
    []
  );

  const clearError = useCallback((accountId: string) => {
    setErrors(prev => {
      const next = new Map(prev);
      next.delete(accountId);
      return next;
    });
  }, []);

  const getErrorsForSource = useCallback(
    (source: ErrorSource): PlatformError[] => {
      return Array.from(errors.values()).filter(e => e.source === source);
    },
    [errors]
  );

  const value = useMemo(
    () => ({ errors, reportError, clearError, getErrorsForSource }),
    [errors, reportError, clearError, getErrorsForSource]
  );

  return (
    <PlatformErrorContext.Provider value={value}>
      {children}
    </PlatformErrorContext.Provider>
  );
}

interface WidgetSourceProviderProps {
  source: ErrorSource;
  children: ReactNode;
}

/**
 * Provider for widget source context.
 *
 * Wraps container-level components to identify their error source.
 *
 * @example
 * <WidgetSourceProvider source="dashboard">
 *   <DashboardErrorBanner />
 *   <WidgetGrid widgets={widgets} />
 * </WidgetSourceProvider>
 */
export function WidgetSourceProvider({
  source,
  children,
}: WidgetSourceProviderProps) {
  return (
    <WidgetSourceContext.Provider value={source}>
      {children}
    </WidgetSourceContext.Provider>
  );
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

/**
 * Hook for reporting and accessing platform-level errors.
 *
 * Source is automatically detected from WidgetSourceProvider context.
 *
 * @throws Error if used outside PlatformErrorProvider
 *
 * @example
 * function WidgetPackageErrorBoundary() {
 *   const { reportError, clearError } = usePlatformErrors();
 *
 *   // Report error with automatic source injection
 *   reportError(accountId, classifiedError);
 *
 *   // Clear after successful re-auth
 *   clearError(accountId);
 * }
 */
export function usePlatformErrors() {
  const ctx = useContext(PlatformErrorContext);
  const source = useContext(WidgetSourceContext);

  if (!ctx) {
    throw new Error(
      'usePlatformErrors must be used within PlatformErrorProvider. ' +
        'Ensure your component is rendered inside the app Providers.'
    );
  }

  const reportError = useCallback(
    (accountId: string, error: ClassifiedError) => {
      // Inject source into error details before reporting
      const errorWithSource: ClassifiedError = {
        ...error,
        details: {
          ...error.details,
          source,
        },
      };
      ctx.reportError(accountId, errorWithSource);
    },
    [ctx, source]
  );

  return {
    errors: ctx.errors,
    reportError,
    clearError: ctx.clearError,
    getErrorsForSource: ctx.getErrorsForSource,
  };
}

/**
 * Hook to get the current widget source context.
 *
 * @returns Current error source ('dashboard' or 'global')
 *
 * @example
 * function DashboardErrorBanner() {
 *   const source = useWidgetSource();
 *   const { getErrorsForSource } = usePlatformErrors();
 *   const errors = getErrorsForSource(source);
 *   // ...
 * }
 */
export function useWidgetSource(): ErrorSource {
  return useContext(WidgetSourceContext);
}

// Re-export ErrorSource type for convenience
export type { ErrorSource } from '../types/classified-error';
